/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useMediaQuery } from 'hooks/useMediaQuery';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Video } from 'ui/atoms/Video';
import { Image } from 'ui/atoms/Image';

import styles from './CompanyProfile.module.scss';

import { data } from './data';
import type { ItemProps, Marker } from './CompanyProfile.types';

const baseClass = 'company-profile';

const ITEM_HEIGHT = 850;

export type Props = {
  title: string;
  description: string;
  className?: string;
  marker?: Marker;
};

export const CompanyProfile = ({ title, description, className, marker = 'number' }: Props) => {
  const greaterThanSmallDesktop = useMediaQuery(768);

  const containerRef = useRef<HTMLDivElement>(null);
  const [expandedItemIndex, setExpandedItemIndex] = useState<number>(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const totalHeight = useRef(data.length * ITEM_HEIGHT);

  const scrollToItem = (index: number) => {
    if (!containerRef.current || !greaterThanSmallDesktop) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerTop = containerRect.top + window.scrollY;
    const containerHeight = containerRect.height;
    const viewportHeight = window.innerHeight;

    // Calculate the target scroll position to center the item
    const elementCount = data.length;
    const BUFFER = 0.1;

    // Calculate what scroll progress we need for this index
    const progressPerElement = (1 - 2 * BUFFER) / elementCount;
    const targetProgress = BUFFER + index * progressPerElement + progressPerElement / 2;

    // Convert progress to actual scroll position
    const targetScrollY = containerTop - viewportHeight + targetProgress * (containerHeight + viewportHeight);

    window.scrollTo({
      top: targetScrollY,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (!greaterThanSmallDesktop) {
      return undefined;
    }

    const handleScroll = () => {
      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const containerTop = rect.top;
      const containerHeight = rect.height;
      const viewportHeight = window.innerHeight;

      // Calculate how much of the container has been scrolled through
      const scrollProgress = Math.max(
        0,
        Math.min(1, (viewportHeight - containerTop) / (containerHeight + viewportHeight)),
      );

      const elementCount = data.length;
      const BUFFER = 0.1; // Buffer zone for first and last elements

      let currentIndex;

      // Add buffer zones for first and last elements
      if (scrollProgress <= BUFFER) {
        currentIndex = 0;
      } else if (scrollProgress >= 1 - BUFFER) {
        currentIndex = elementCount - 1;
      } else {
        // Divide the remaining scroll progress equally among all elements
        const adjustedProgress = (scrollProgress - BUFFER) / (1 - 2 * BUFFER);
        const progressPerElement = 1 / elementCount;
        currentIndex = Math.min(elementCount - 1, Math.floor(adjustedProgress / progressPerElement));
      }

      setExpandedItemIndex(currentIndex);
      setCurrentVideoIndex(currentIndex);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [greaterThanSmallDesktop]);

  return (
    <section ref={containerRef} className={cn(styles[`${baseClass}-wrapper`], className)}>
      <div className={styles[`${baseClass}-container`]}>
        <TitleBlock title={title} description={description} />

        <div
          className={styles[`${baseClass}-content`]}
          style={greaterThanSmallDesktop ? { height: totalHeight.current } : undefined}
        >
          <div className={styles[`${baseClass}-left`]}>
            {data[currentVideoIndex]?.videoUrl ? (
              <Video src={data[currentVideoIndex].videoUrl} className={styles[`${baseClass}-video`]} />
            ) : (
              <Image width={480} height={332} src={data[currentVideoIndex]?.imageUrl} alt="Video preview" />
            )}
          </div>

          <div className={styles[`${baseClass}-right`]}>
            {data.map((section, index) => {
              const sectionNumber = index + 1;

              return (
                <>
                  {!greaterThanSmallDesktop && (
                    <>
                      {data[currentVideoIndex]?.videoUrl ? (
                        <Video src={data[currentVideoIndex].videoUrl} className={styles[`${baseClass}-video`]} />
                      ) : (
                        <Image width={480} height={332} src={data[currentVideoIndex]?.imageUrl} alt="Video preview" />
                      )}
                    </>
                  )}

                  <Item
                    key={index}
                    number={sectionNumber}
                    {...section}
                    marker={marker}
                    isExpanded={!greaterThanSmallDesktop || expandedItemIndex === index}
                    onClick={() => scrollToItem(index)}
                  />
                </>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const Item = ({
  number,
  title,
  items,
  isExpanded,
  onClick,
  marker = 'number',
}: ItemProps & { onClick?: () => void }) => {
  const isCheck = marker === 'check';

  return (
    <div
      className={cn(
        styles[`${baseClass}-item`],
        isExpanded && styles[`${baseClass}-item-active`],
        isCheck && styles[`${baseClass}-item-check`],
      )}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <div className={styles[`${baseClass}-item-content`]}>
        {isCheck ? (
          <Icon type={isExpanded ? 'check-7' : 'check-6'} className={styles[`${baseClass}-item-icon`]} />
        ) : (
          <span className={styles[`${baseClass}-item-number`]}>{number.toString().padStart(2, '0')}</span>
        )}

        <div className={styles[`${baseClass}-item-text`]}>
          <span>{title}</span>

          {items && (
            <ul className={cn(styles[`${baseClass}-item-list`], isExpanded && styles[`${baseClass}-item-list-active`])}>
              {items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
